import { useCallback, useEffect } from "react";
import { setPlaceHeld } from "./boardDragPlace";
import type { BoardDraggingState, BoardDragState } from "./boardDragState";
import { isPlaceModifier } from "./boardSensors";
import type { LatestBoardDragArgs } from "./useBoardDragStore";

interface UseBoardPlaceModifierArgs<TItem> {
    stateRef: { readonly current: BoardDragState<TItem> };
    latestRef: { readonly current: LatestBoardDragArgs<TItem> };
    commit: (next: BoardDragState<TItem>) => void;
    replayHover: (current: BoardDraggingState) => void;
    isPointerDrag: boolean;
}

const CAPTURE = { capture: true };

/**
 * Follows the place key (⌘, Ctrl off Mac) through a pointer drag. Pointer
 * moves carry it, and key presses update it without a move, so pressing or
 * releasing it alone swaps a sorted cell between its hint and a slot under
 * the pointer.
 */
export const useBoardPlaceModifier = <TItem>({
    stateRef,
    latestRef,
    commit,
    replayHover,
    isPointerDrag,
}: UseBoardPlaceModifierArgs<TItem>) => {
    const follow = useCallback(
        (event: PointerEvent | KeyboardEvent) => {
            const current = stateRef.current;
            if (current.phase !== "dragging" || current.isKeyboard) {
                return;
            }
            const next = setPlaceHeld({
                model: latestRef.current,
                current,
                isPlaceHeld: isPlaceModifier(event),
            });
            if (next !== current) {
                commit(next);
                replayHover(next);
            }
        },
        [commit, latestRef, replayHover, stateRef],
    );

    useEffect(() => {
        if (!isPointerDrag) {
            return undefined;
        }
        window.addEventListener("pointermove", follow, CAPTURE);
        window.addEventListener("keydown", follow, CAPTURE);
        window.addEventListener("keyup", follow, CAPTURE);
        return () => {
            window.removeEventListener("pointermove", follow, CAPTURE);
            window.removeEventListener("keydown", follow, CAPTURE);
            window.removeEventListener("keyup", follow, CAPTURE);
        };
    }, [follow, isPointerDrag]);
};
